import { clearUserProfileCache } from '../utils/userProfileCache';
import { clearUserContext } from '../telemetry/appinsights';
import { apiUrl } from './apiBase';

const LOGOUT_TIMEOUT_MS = 3000;

export class ApiRequestError extends Error {
  constructor(message, { status = 0, data = null, response = null, aborted = false } = {}) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.data = data;
    this.response = response;
    this.aborted = aborted;
    this.isAuthenticationError = status === 401;
    this.isAuthorizationError = status === 403;
    this.isConflictError = status === 409;
    this.isRateLimitError = status === 429;
    this.isServerError = status >= 500;
    this.isAuthError = this.isAuthenticationError;
  }
}

export async function parseResponseJson(response) {
  if (!response || response.status === 204) return null;
  if (typeof response.text !== 'function') {
    if (typeof response.json !== 'function') return null;
    try {
      return await response.json();
    } catch {
      throw new ApiRequestError('The server returned an unreadable response.', {
        status: response?.status || 0,
        response
      });
    }
  }
  const text = await response.text().catch(() => '');
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    if (response.ok === false) {
      return { message: text.slice(0, 500) };
    }
    throw new ApiRequestError('The server returned an unreadable response.', {
      status: response?.status || 0,
      response
    });
  }
}

export function clearProtectedUserState({ setUserName } = {}) {
  clearUserProfileCache();
  localStorage.removeItem('userName');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('subscriptionStatus');
  localStorage.removeItem('role');
  localStorage.removeItem('subscribedSubject');
  localStorage.removeItem('subscribedGrade');
  localStorage.removeItem('subscribedPlan');
  localStorage.removeItem('pendingReferralRedemptionRequest');
  Promise.resolve(clearUserContext()).catch(() => {});
  if (typeof setUserName === 'function') setUserName('');
}

export function resetSessionExpirationRedirectGuard() {
  handleSessionExpired.redirecting = false;
}

export async function revokeBackendSession() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS);
  try {
    await fetch(apiUrl('/api/logoutUser'), {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal
    });
  } catch {
    // Frontend state is still cleared by the caller. Backend revocation must be verified before production.
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function handleSessionExpired({ navigate, setUserName, message } = {}) {
  clearProtectedUserState({ setUserName });
  if (handleSessionExpired.redirecting) return;
  if (typeof navigate === 'function' && window.location.pathname !== '/login') {
    handleSessionExpired.redirecting = true;
    navigate('/login', {
      replace: true,
      state: { message: message || 'Your session expired. Please log in again.' }
    });
    window.setTimeout(() => {
      handleSessionExpired.redirecting = false;
    }, 0);
  }
}

export async function authenticatedFetch(url, options = {}) {
  let response;
  try {
    response = await fetch(apiUrl(url), {
      ...options,
      credentials: 'include',
      headers: {
        ...(options.headers || {})
      }
    });
  } catch (error) {
    throw new ApiRequestError(error?.message || 'Network request failed.', {
      status: 0,
      aborted: error?.name === 'AbortError'
    });
  }
  const data = await parseResponseJson(response);
  if (!response.ok) {
    throw new ApiRequestError(data?.error || data?.message || response.statusText || `Request failed: ${response.status}`, {
      status: response.status,
      data,
      response
    });
  }
  return data;
}
